import { FieldSet, InputWizardPanelComponent, TextInput } from "o4a-react";
import * as React from "react";
import * as Messages from "../../codegen/Messages";
import { RegionSelect } from "../../components/RegionSelect/RegionSelect";
import { ResourceGroupSelect } from "../../components/ResourceGroupSelect/ResourceGroupSelect";
import { SubscriptionSelect } from "../../components/SubscriptionSelect/SubscriptionSelect";
import { nameCharMaxLength, nameCharMinLength, nameValidationRegex } from "../../helpers/validationHelper";

export const GROUP_BASICS = "basics";

export enum Fields {
  Subscription = "subscriptionId",
  ResourceGroup = "resourceGroupId",
  Name = "cloudVmClusterDetails.name",
  Region = "region",
}

export enum FieldTestIds {
  Subscription = "subscription",
  Name = "name",
  ResourceGroup = "resourceGroup",
  Region = "region"
}

export interface BasicsTabComponent {
  closeSidePanels: () => void;
}
export interface BasicsTabContentProps {
  location?: string;
  subscription?: string;
  resourceGroupId?: string;
  disableUIControls?: Fields[] | undefined;
  inputWizardPanelRef: InputWizardPanelComponent;
}

export const BasicsTabContent = (
  {
    location, subscription, resourceGroupId, disableUIControls,
    inputWizardPanelRef,
  }: BasicsTabContentProps,
): JSX.Element => {
  const [subscriptionId, setSubscriptionId] = React.useState<string>(subscription || "");

  const onSubscriptionChange = (subId: string): void => setSubscriptionId(subId);

  const onResourceGroupError = (): void => {
    inputWizardPanelRef.showError(Messages.createCommon.loadingErrors.resourceGroups());
  };

  const onResourceGroupMissingDependencies = (): void => inputWizardPanelRef.showErrorDialog(
    Messages.createCommon.dependencyWarnings.subscriptionNotSelected.message(),
    Messages.createCommon.dependencyWarnings.subscriptionNotSelected.title(),
  );

  return (
    <>
      <FieldSet header={Messages.createInfra.basicsTab.sectionTitles.projectDetails()}>
        <SubscriptionSelect
          required
          onChange={onSubscriptionChange}
          fieldName={Fields.Subscription}
          testId={FieldTestIds.Subscription}
          label={Messages.labels.subscription()}
          tooltip={Messages.hints.toolTipSubscription()}
          disabled={disableUIControls?.find(ele => ele === Fields.Subscription) !== undefined}
          defaultValue={subscriptionId ? [subscriptionId] : undefined}
        />
        <ResourceGroupSelect
          required
          subField
          testId={FieldTestIds.ResourceGroup}
          subscriptionId={subscriptionId}
          fieldName={Fields.ResourceGroup}
          label={Messages.labels.resourceGroup()}
          tooltip={Messages.hints.toolTipResourceGroup()}
          onError={onResourceGroupError}
          onMissingDependencies={onResourceGroupMissingDependencies}
          defaultValue={resourceGroupId ? [resourceGroupId] : undefined}
          disabled={disableUIControls?.find(ele => ele === Fields.ResourceGroup) !== undefined}
        />
      </FieldSet>
      <FieldSet header={Messages.createInfra.basicsTab.sectionTitles.instanceDetails()}>
        <TextInput
          required
          testId={FieldTestIds.Name}
          fieldName={Fields.Name}
          label={Messages.labels.name()}
          validator={(value: string | undefined) => {
            const errors: string[] = [];
            if (!value || (value?.length < nameCharMinLength || value?.length > nameCharMaxLength)) {
              errors.push(Messages.validation.nameChars(nameCharMinLength.toString(), nameCharMaxLength.toString()));
            }
            if (!value || (value && !nameValidationRegex.test(value))) {
              errors.push(Messages.validation.nameValidation());
            }
            return errors.length > 0 ? errors : undefined;
          }}
        />
        <RegionSelect
          required
          testId={FieldTestIds.Region}
          subscriptionId={subscriptionId}
          // showUnlinkedRegions
          fieldName={Fields.Region}
          label={Messages.labels.region()}
          tooltip={Messages.hints.toolTipRegion()}
          defaultValue={location ? [location] : undefined}
          disabled={disableUIControls?.find(ele => ele === Fields.Region) !== undefined}
        />
      </FieldSet>
    </>
  );
};
